import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { API_CACHE_TTL, fetchCachedGetApi } from "../../../utils/apiCache";
import { isFanHubPagePath, resolveFanHubArtistUsername } from "../../../utils/fanHubRoutes";
import { useArtistGateLock } from "../../gate/useArtistGateLock";
import type { SpotifyAlbumSummary } from "../../../utils/fanFeatures";
import { FanHub } from "./FanHub";

interface ArtistData {
  name: string;
  images?: Array<{ url: string }>;
}

export function FanHubMount() {
  const location = useLocation();
  const gate = useArtistGateLock();
  const shouldShowFanHub = useMemo(
    () => isFanHubPagePath(location.pathname) && !gate.locked && !gate.resolving,
    [location.pathname, gate.locked, gate.resolving],
  );
  const artistUsername = useMemo(() => resolveFanHubArtistUsername(location.pathname), [location.pathname]);
  const [artistData, setArtistData] = useState<ArtistData | null>(null);
  const [albums, setAlbums] = useState<SpotifyAlbumSummary[]>([]);

  useEffect(() => {
    if (!shouldShowFanHub) {
      setArtistData(null);
      setAlbums([]);
      return;
    }

    let cancelled = false;

    const loadFanHubData = async () => {
      try {
        setArtistData(null);
        setAlbums([]);

        const artistResponse = await fetchCachedGetApi<{ artistData?: ArtistData }>("/.netlify/functions/SpotifyGetArtist", { artist: artistUsername }, {
          ttlMs: API_CACHE_TTL.SIX_HOURS,
          shouldCache: (data) => Boolean(data?.artistData),
        });

        if (cancelled) return;
        const nextArtistData = artistResponse.artistData || null;
        setArtistData(nextArtistData);
        if (!nextArtistData) return;

        const albumsResponse = await fetchCachedGetApi<{ albums?: SpotifyAlbumSummary[] }>("/.netlify/functions/SpotifyGetArtistAlbums", { artist: artistUsername }, {
          ttlMs: API_CACHE_TTL.SIX_HOURS,
          shouldCache: (data) => Array.isArray(data?.albums),
        });

        if (cancelled) return;
        setAlbums(albumsResponse.albums || []);
      } catch (error) {
        if (cancelled) return;
        setArtistData(null);
        setAlbums([]);
      }
    };

    loadFanHubData();

    return () => {
      cancelled = true;
    };
  }, [artistUsername, shouldShowFanHub]);

  if (!shouldShowFanHub || !artistData || albums.length === 0) return null;

  return <FanHub artistData={artistData} albums={albums} />;
}
